import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { Button } from '../button/button';
import { CopyIcon } from '../copy-icon/copy-icon';
import { chargebackDisputeReasonDisplay } from '../../models/chargeback-dispute-reason';
import {
  ChargebackStatus,
  type ChargebackStatusTone,
  chargebackStatusDisplay,
  chargebackStatusMerchantBannerMessage,
  chargebackStatusTone,
  isChargebackTerminalForMerchant,
  isMerchantChargebackActionable,
  isOfficerChargebackActionable,
  parseChargebackStatus,
  parseChargebackStatusFromRecord,
} from '../../models/chargeback-status';
import { Notification } from '@shared';

/** Normalized attachment for evidence repository UI. */
export interface ChargebackEvidenceItem {
  title: string;
  subtitle: string;
  kind: 'pdf' | 'image' | 'other';
  action: 'download' | 'view';
  url?: string;
}

export type ViewChargebackDialogResult = 'accept' | 'decline';

/** Returned when an officer uses Approve / Decline on the case detail drawer. */
export type ViewChargebackDialogOfficerActionResult = {
  officerAction: 'approve' | 'decline';
};

export type ViewChargebackDialogCloseResult =
  | ViewChargebackDialogResult
  | ViewChargebackDialogOfficerActionResult;

export interface ViewChargebackDialogData {
  chargeback: Record<string, any>;
  showActions?: boolean;
  viewerRole?: 'merchant' | 'officer';
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  /** When true and role is officer, show approve/decline (only if status is officer-actionable). */
  showOfficerActions?: boolean;
  officerPrimaryActionLabel?: string;
  officerSecondaryActionLabel?: string;
}

@Component({
  selector: 'lib-view-chargeback-dialog',
  imports: [Button, CopyIcon, MatDialogClose, DatePipe],
  templateUrl: './view-chargeback-dialog.html',
  styleUrl: './view-chargeback-dialog.css',
})
export class ViewChargebackDialog {
  readonly data = inject<ViewChargebackDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(
    MatDialogRef<ViewChargebackDialog, ViewChargebackDialogCloseResult>,
  );
  private readonly datePipe = inject(DatePipe);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly notify = inject(Notification);

  /** Parsed workflow status when the API sends a known code or name. */
  readonly parsedStatus: ChargebackStatus | null =
    parseChargebackStatusFromRecord(this.data.chargeback);

  get viewerRole(): 'merchant' | 'officer' {
    return (
      this.data.viewerRole ??
      (this.data.showActions === false ? 'officer' : 'merchant')
    );
  }

  get actionDeadLine() {
    return this.data.chargeback['actionDeadline'] || '';
  }

  get showFooterActions(): boolean {
    if (this.data.showActions === false) {
      return false;
    }
    if (this.viewerRole !== 'merchant') {
      return false;
    }
    if (this.parsedStatus == null) {
      return true;
    }
    return isMerchantChargebackActionable(this.parsedStatus);
  }

  /** Officer approve/decline footer (admin workflow). */
  get showOfficerFooterActions(): boolean {
    if (this.viewerRole !== 'officer') {
      return false;
    }
    if (this.data.showOfficerActions !== true) {
      return false;
    }
    if (this.parsedStatus == null) {
      return false;
    }
    return isOfficerChargebackActionable(this.parsedStatus);
  }

  get officerPrimaryLabel(): string {
    return this.data.officerPrimaryActionLabel ?? 'Approve';
  }

  get officerSecondaryLabel(): string {
    return this.data.officerSecondaryActionLabel ?? 'Decline';
  }

  get stateBanner(): {
    tone: ChargebackStatusTone | 'officer';
    message: string;
  } | null {
    if (this.viewerRole === 'officer') {
      if (this.showOfficerFooterActions) {
        return {
          tone: 'info',
          message:
            'Review the case and evidence below. Approve to authorize the refund amount, or decline with a reason.',
        };
      }
      return {
        tone: 'officer',
        message:
          'Read-only view. Approve and decline are available when the case is pending officer decision.',
      };
    }
    if (this.showFooterActions) {
      return null;
    }
    if (this.parsedStatus != null) {
      const message = chargebackStatusMerchantBannerMessage(this.parsedStatus);
      if (message) {
        return { tone: chargebackStatusTone(this.parsedStatus), message };
      }
    }
    return {
      tone: 'neutral',
      message: 'This case is not open for accept or decline from this screen.',
    };
  }

  get statusChipClass(): string {
    if (this.parsedStatus == null) {
      return 'vb-status-chip vb-status-chip--neutral';
    }
    return `vb-status-chip vb-status-chip--${chargebackStatusTone(this.parsedStatus)}`;
  }

  get daysRowLabel(): string {
    if (
      this.parsedStatus != null &&
      isChargebackTerminalForMerchant(this.parsedStatus)
    ) {
      return 'Case status';
    }
    return 'Days';
  }

  get daysRowValue(): string {
    if (
      this.parsedStatus != null &&
      isChargebackTerminalForMerchant(this.parsedStatus)
    ) {
      return chargebackStatusDisplay(this.parsedStatus);
    }
    return this.daysLabel;
  }

  get daysRowValueClass(): string {
    if (
      this.parsedStatus != null &&
      isChargebackTerminalForMerchant(this.parsedStatus)
    ) {
      return 'text-sm font-medium text-[#434C53]';
    }
    if (
      this.parsedStatus != null &&
      isMerchantChargebackActionable(this.parsedStatus)
    ) {
      return 'text-sm font-semibold text-[#E71D36]';
    }
    return 'text-sm font-medium text-[#17191C]';
  }

  get parsedStatusLabel(): string | null {
    if (this.parsedStatus == null) {
      return null;
    }
    return chargebackStatusDisplay(this.parsedStatus);
  }

  get primaryLabel(): string {
    return this.data.primaryActionLabel ?? 'Approve Chargeback';
  }

  get secondaryLabel(): string {
    return this.data.secondaryActionLabel ?? 'Decline';
  }

  get headerName(): string {
    const c = this.data.chargeback;
    const name = c['customerName'] ?? c['customerFullName'];
    if (typeof name === 'string' && name.trim()) {
      return name.trim();
    }
    const email = c['customerEmail'];
    if (typeof email === 'string' && email.includes('@')) {
      return email.split('@')[0] ?? String(email);
    }
    return typeof email === 'string' ? email : 'Customer';
  }

  get caseRef(): string {
    return this.str('caseRef', 'caseReference', 'reference');
  }

  get daysLabel(): string {
    const raw =
      this.data.chargeback['daysOpen'] ??
      this.data.chargeback['daysRemaining'] ??
      this.data.chargeback['slaDays'] ??
      this.data.chargeback['days'];
    if (raw === null || raw === undefined || raw === '') {
      return '—';
    }
    return String(raw);
  }

  get formattedDate(): string {
    const c = this.data.chargeback;
    const v = c['dateRaised'];
    if (v == null || v === '') {
      return '—';
    }
    const d = new Date(String(v));
    if (!Number.isNaN(d.getTime())) {
      return this.datePipe.transform(d, 'medium') ?? String(v);
    }
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
    return '—';
  }

  get transactionId(): string {
    return this.str('transactionId', 'transactionID', 'transactionRef');
  }

  get merchantName(): string {
    return this.str('merchantName', 'businessName', 'merchantBusinessName');
  }

  get customerName(): string {
    const c = this.data.chargeback;
    const name = c['customerName'] ?? c['customerFullName'];
    if (typeof name === 'string' && name.trim()) {
      return name.trim();
    }
    const email = c['customerEmail'];
    return typeof email === 'string' ? email : '—';
  }

  get amountDisplay(): string {
    const c = this.data.chargeback;
    const amt = c['amount'] ?? c['disputedAmount'] ?? c['refundAmount'];
    if (typeof amt === 'number') {
      return (
        this.currencyPipe.transform(amt, 'NGN', '₦', '1.2-2') ?? String(amt)
      );
    }
    if (typeof amt === 'string' && amt.trim()) {
      return amt.trim();
    }
    return '—';
  }

  get resolvedProvider(): string {
    return this.str(
      'provider',
      'bankName',
      'acquirerBankName',
      'virtualAccountNumber',
    );
  }

  get reasonTextCore(): string {
    const c = this.data.chargeback;
    const codedReasonKeys = [
      'reason',
      'disputeReason',
      'customerReason',
      'chargebackReason',
    ] as const;
    for (const k of codedReasonKeys) {
      const v = c[k];
      if (v !== null && v !== undefined && v !== '') {
        const label = chargebackDisputeReasonDisplay(v);
        if (label) {
          return label;
        }
      }
    }
    const desc = c['description'];
    if (typeof desc === 'string' && desc.trim()) {
      return desc.trim();
    }
    return '';
  }

  get customerDescriptionText(): string {
    return this.data.chargeback['description'] as string;
  }

  get merchantResponseText(): string {
    const c = this.data.chargeback as {
      evidence?: { merchantResponse?: unknown };
    };
    const v = c?.evidence?.merchantResponse;
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
    return '';
  }

  get statusTagPrimary(): string {
    const s = this.data.chargeback['customerRaisedLabel'];
    if (typeof s === 'string' && s.trim()) {
      return s.trim();
    }
    return 'Customer Raised';
  }

  get statusTagSecondary(): string {
    const s =
      this.data.chargeback['reviewStatusLabel'] ??
      this.data.chargeback['statusLabel'];
    if (typeof s === 'string' && s.trim()) {
      return s.trim();
    }
    return this.formatStatusFallback();
  }

  get merchantEvidenceItems(): ChargebackEvidenceItem[] {
    const ev = (this.data.chargeback as { evidence?: unknown }).evidence as
      | { merchantFiles?: unknown }
      | undefined;
    return this.normalizeEvidenceList(ev?.merchantFiles);
  }

  get customerEvidenceItems(): ChargebackEvidenceItem[] {
    const ev = (this.data.chargeback as { evidence?: unknown }).evidence as
      | { customerFiles?: unknown }
      | undefined;
    return this.normalizeEvidenceList(ev?.customerFiles);
  }

  get statusBadges(): unknown[] {
    const b = this.data.chargeback['statusBadges'];
    return Array.isArray(b) ? b : [];
  }

  onAccept(): void {
    this.dialogRef.close('accept' as ViewChargebackDialogResult);
  }

  onDecline(): void {
    this.dialogRef.close('decline' as ViewChargebackDialogResult);
  }

  onOfficerApprove(): void {
    this.dialogRef.close({ officerAction: 'approve' });
  }

  onOfficerDecline(): void {
    this.dialogRef.close({ officerAction: 'decline' });
  }

  openLink(url: string | undefined): void {
    if (url?.trim()) {
      window.open(url.trim(), '_blank', 'noopener,noreferrer');
    }
  }

  copyTransactionId(): void {
    const id = this.transactionId;
    if (id === '—' || !id) {
      return;
    }
    void navigator.clipboard?.writeText(id);
    this.notify.info('Transaction ID copied to clipboard');
  }

  private formatStatusFallback(): string {
    const raw =
      this.data.chargeback['status'] ??
      this.data.chargeback['chargebackStatus'] ??
      this.data.chargeback['caseStatus'];
    if (raw === null || raw === undefined) {
      return chargebackStatusDisplay(ChargebackStatus.UnderReview);
    }
    const parsed = parseChargebackStatus(raw, { caseInsensitive: true });
    if (parsed != null) {
      return chargebackStatusDisplay(parsed);
    }
    const s = String(raw)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  private str(...keys: string[]): string {
    const c = this.data.chargeback;
    for (const k of keys) {
      const v = c[k];
      if (v !== null && v !== undefined && String(v).trim() !== '') {
        return String(v);
      }
    }
    return '—';
  }

  private normalizeEvidenceList(raw: unknown): ChargebackEvidenceItem[] {
    if (!Array.isArray(raw) || raw.length === 0) {
      return [];
    }
    return raw.map((item) => this.normalizeEvidenceItem(item));
  }

  private normalizeEvidenceItem(item: unknown): ChargebackEvidenceItem {
    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (!trimmed) {
        return {
          title: 'Attachment',
          subtitle: '',
          kind: 'other',
          action: 'download',
        };
      }
      if (/^https?:\/\//i.test(trimmed)) {
        const title = this.fileNameFromEvidenceUrl(trimmed);
        const lower = title.toLowerCase();
        const kind = this.evidenceKindFromName(lower, '');
        const action: ChargebackEvidenceItem['action'] =
          kind === 'image' ? 'view' : 'download';
        return {
          title,
          subtitle: '',
          kind,
          action,
          url: trimmed,
        };
      }
      return {
        title: trimmed.length > 48 ? `${trimmed.slice(0, 45)}…` : trimmed,
        subtitle: '',
        kind: 'other',
        action: 'download',
      };
    }
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      const title = String(o['title'] ?? o['name'] ?? o['fileName'] ?? 'File');
      const subtitle = String(
        o['subtitle'] ?? o['meta'] ?? o['description'] ?? '',
      );
      const url =
        typeof o['url'] === 'string'
          ? o['url'].trim()
          : typeof o['downloadUrl'] === 'string'
            ? (o['downloadUrl'] as string).trim()
            : undefined;
      const mime = String(o['mimeType'] ?? o['type'] ?? '').toLowerCase();
      const kind: ChargebackEvidenceItem['kind'] = mime.includes('image')
        ? 'image'
        : mime.includes('pdf') || title.toLowerCase().endsWith('.pdf')
          ? 'pdf'
          : 'other';
      const action: ChargebackEvidenceItem['action'] =
        kind === 'image' ? 'view' : 'download';
      return { title, subtitle, kind, action, url };
    }
    return {
      title: 'Attachment',
      subtitle: '',
      kind: 'other',
      action: 'download',
    };
  }

  private fileNameFromEvidenceUrl(url: string): string {
    try {
      const path = new URL(url).pathname;
      const last = path.split('/').filter(Boolean).pop();
      if (last) {
        return decodeURIComponent(last);
      }
    } catch {
      /* fall through */
    }
    const afterSlash = url.split('/').filter(Boolean).pop();
    return afterSlash
      ? decodeURIComponent(afterSlash.split('?')[0] ?? '')
      : 'File';
  }

  private evidenceKindFromName(
    lowerName: string,
    mime: string,
  ): ChargebackEvidenceItem['kind'] {
    if (mime.includes('image')) {
      return 'image';
    }
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(lowerName)) {
      return 'image';
    }
    if (mime.includes('pdf') || lowerName.endsWith('.pdf')) {
      return 'pdf';
    }
    return 'other';
  }
}
